import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ClipboardList,
  BarChart3,
  LogOut,
  Timer,
  Power,
  Menu,
  X,
  UtensilsCrossed,
  Boxes,
  Tag,
  Megaphone,
  Wallet,
  Building2,
  PauseCircle,
  PlayCircle,
  Calculator,
  Palette,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { updateStore, errMsg } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { HoursBadge } from "@/components/HoursBadge";
import { LEGAL_NAME, WEBSITE } from "@/lib/brand";
import { cn } from "@/lib/utils";

const PREP_OPTIONS = [10, 15, 20, 25, 30, 40, 45, 60];

const NavItem = ({ to, icon: Icon, label, testid, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    data-testid={testid}
    className={({ isActive }) =>
      cn(
        "flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm font-medium transition-colors",
        isActive
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
      )
    }
  >
    <Icon className="h-4 w-4" />
    {label}
  </NavLink>
);

export default function Shell() {
  const { user, vendor, logout, refreshVendor, patchVendorLocal } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [snoozeUntil, setSnoozeUntil] = useState(() => Number(localStorage.getItem("vendor_snooze") || 0));
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    refreshVendor().catch((e) => {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        logout();
        navigate("/login");
      }
    });
  }, [refreshVendor, logout, navigate]);

  const toggleOpen = async (open) => {
    if (open) {
      setSnoozeUntil(0);
      localStorage.removeItem("vendor_snooze");
    }
    patchVendorLocal({ open });
    try {
      await updateStore({ open });
      toast.success(open ? "Store is now OPEN" : "Store is now CLOSED");
    } catch (e) {
      patchVendorLocal({ open: !open });
      toast.error(errMsg(e));
    }
  };

  const snooze = async (mins) => {
    const until = Date.now() + mins * 60000;
    setSnoozeUntil(until);
    localStorage.setItem("vendor_snooze", String(until));
    patchVendorLocal({ open: false });
    try {
      await updateStore({ open: false });
      toast.success(`Snoozed for ${mins} min — auto-reopens after`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const reopenNow = async () => {
    setSnoozeUntil(0);
    localStorage.removeItem("vendor_snooze");
    patchVendorLocal({ open: true });
    try {
      await updateStore({ open: true });
      toast.success("Store reopened");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // Snooze ticker + auto-reopen when the window elapses.
  useEffect(() => {
    if (!snoozeUntil) return;
    const id = setInterval(() => {
      if (Date.now() >= snoozeUntil) {
        setSnoozeUntil(0);
        localStorage.removeItem("vendor_snooze");
        patchVendorLocal({ open: true });
        updateStore({ open: true })
          .then(() => toast.success("Snooze ended — store reopened"))
          .catch(() => {});
      } else {
        setTick(Date.now());
      }
    }, 1000);
    return () => clearInterval(id);
  }, [snoozeUntil, patchVendorLocal]);

  const changePrep = async (val) => {
    const prep_time_min = parseInt(val, 10);
    patchVendorLocal({ prep_time_min });
    setSaving(true);
    try {
      await updateStore({ prep_time_min });
      toast.success("Default prep time set to " + prep_time_min + " min");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const isOpen = !!vendor?.open;
  const snoozeActive = snoozeUntil > tick;
  const snoozeRem = snoozeActive ? snoozeUntil - tick : 0;
  const smm = Math.floor(snoozeRem / 60000);
  const sss = Math.floor((snoozeRem % 60000) / 1000);

  const storeStatus = (() => {
    if (snoozeActive) return { label: "Snoozed", accent: "text-amber-600", border: "border-amber-400 bg-amber-50" };
    if (isOpen) return { label: "Store Open", accent: "text-primary", border: "border-primary/40 bg-accent" };
    return { label: "Store Closed", accent: "text-destructive", border: "border-destructive/40 bg-destructive/5" };
  })();

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-6 pb-5 border-b border-border">
        <BrandMark textClass="text-xl" />
        <div className="mt-4">
          <p className="font-display font-bold text-lg leading-tight truncate" data-testid="shop-name">
            {vendor?.shop_name || "My Shop"}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {vendor?.city || ""} {vendor?.pincode ? "· " + vendor.pincode : ""}
          </p>
          <HoursBadge />
        </div>
      </div>

      <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
        <NavItem to="/orders" icon={ClipboardList} label="Live Orders" testid="nav-orders" onClick={() => setMobileOpen(false)} />
        <NavItem to="/menu" icon={UtensilsCrossed} label="Menu" testid="nav-menu" onClick={() => setMobileOpen(false)} />
        <NavItem to="/inventory" icon={Boxes} label="Inventory" testid="nav-inventory" onClick={() => setMobileOpen(false)} />
        <NavItem to="/offers" icon={Tag} label="Offers" testid="nav-offers" onClick={() => setMobileOpen(false)} />
        <NavItem to="/ads" icon={Megaphone} label="Ads" testid="nav-ads" onClick={() => setMobileOpen(false)} />
        <NavItem to="/payouts" icon={Wallet} label="Payouts" testid="nav-payouts" onClick={() => setMobileOpen(false)} />
        <NavItem to="/outlet" icon={Building2} label="Outlet" testid="nav-outlet" onClick={() => setMobileOpen(false)} />
        <NavItem to="/fare" icon={Calculator} label="Fare Calc" testid="nav-fare" onClick={() => setMobileOpen(false)} />
        <NavItem to="/analytics" icon={BarChart3} label="Analytics" testid="nav-analytics" onClick={() => setMobileOpen(false)} />
        <NavItem to="/brand-kit" icon={Palette} label="Brand Kit" testid="nav-brand-kit" onClick={() => setMobileOpen(false)} />
      </nav>

      <div className="mt-auto p-3 space-y-3">
        {/* Store status */}
        <div
          className={cn("rounded-md border p-3.5 transition-colors", storeStatus.border)}
          data-testid="store-status-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Power className={cn("h-4 w-4", storeStatus.accent)} />
              <span className="text-sm font-semibold">{storeStatus.label}</span>
            </div>
            <Switch data-testid="store-open-toggle" checked={isOpen} onCheckedChange={toggleOpen} />
          </div>

          {snoozeActive ? (
            <div className="mt-2">
              <p className="text-xs font-semibold text-amber-700 font-mono tabular-nums" data-testid="snooze-countdown">
                Reopening in {smm}:{String(sss).padStart(2, "0")}
              </p>
              <button
                onClick={reopenNow}
                data-testid="reopen-now-btn"
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md border border-primary/40 text-primary text-sm font-semibold hover:bg-accent transition-colors"
              >
                <PlayCircle className="h-3.5 w-3.5" /> Reopen now
              </button>
            </div>
          ) : isOpen ? (
            <div className="mt-2">
              <p className="text-xs text-muted-foreground">Accepting new orders</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => snooze(30)} data-testid="snooze-30-btn" className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md border border-border text-xs font-semibold hover:bg-muted transition-colors">
                  <PauseCircle className="h-3.5 w-3.5" /> 30 min
                </button>
                <button onClick={() => snooze(60)} data-testid="snooze-60-btn" className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md border border-border text-xs font-semibold hover:bg-muted transition-colors">
                  <PauseCircle className="h-3.5 w-3.5" /> 60 min
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mt-1.5">Customers can't order right now</p>
          )}
        </div>

        {/* Default prep time */}
        <div className="rounded-md border border-border p-3.5">
          <label className="flex items-center gap-2 text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium mb-2">
            <Timer className="h-3.5 w-3.5" /> Default prep time
          </label>
          <Select
            value={String(vendor?.prep_time_min || 20)}
            onValueChange={changePrep}
            disabled={saving}
          >
            <SelectTrigger data-testid="default-prep-time-select" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PREP_OPTIONS.map((v) => (
                <SelectItem key={v} value={String(v)}>
                  {v} minutes
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <button
          data-testid="logout-btn"
          onClick={doLogout}
          className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Sign out {user?.name ? "· " + user.name : ""}
        </button>
        <p className="mt-3 px-1 text-[10px] leading-tight text-muted-foreground/70" data-testid="legal-name">
          {LEGAL_NAME}
          <br />
          <a href="https://www.1apponly.com" target="_blank" rel="noreferrer" className="text-primary font-semibold" data-testid="sidebar-website">{WEBSITE}</a>
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 border-r border-border bg-card sticky top-0 h-screen">
        {SidebarInner}
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-card border-b border-border flex items-center justify-between px-4">
        <BrandMark textClass="text-base" iconClass="h-9 w-9" />
        <div className="flex items-center gap-3">
          <span className={cn("text-xs font-semibold", isOpen ? "text-primary" : "text-destructive")}>
            {isOpen ? "OPEN" : "CLOSED"}
          </span>
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)}>
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-card border-r border-border">
            <button
              className="absolute right-3 top-4 z-10"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
            >
              <X className="h-5 w-5" />
            </button>
            {SidebarInner}
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
